import { type ReactNode } from 'react';

export const ERROR_USER_INTERFACE_VALUES = ['page', 'dialog', 'toast'] as const;

export const APP_ERROR_TYPE_VALUES = [
  'badRequest',
  'forbidden',
  'invalidSourceParams',
  'issueNotFound',
  'network',
  'notFound',
  'scanningFile',
  'unauthorized',
  'unauthorizedToViewIssue',
  'unauthorizedToManageIssue',
  'unauthorizedToViewRelationships',
  'userCannotUpdateAction',
  'actionCannotBeApproved',
  'actionCannotBeDeclined',
  'actionCannotBeFinished',
  'actionCannotBeStarted',
  'mutationError',
  'issueAlreadyClosedError',
  'issueSLAAlreadyDisabledError',
  'issueSLAAlreadyEnabledError',
  'issueTemplateDueDateIsGreaterThanSLA',
  'issueTemplateAlreadyExists',
  'actionDueDateMustBeLessOrEqualThanIssueTemplateDueDate',
  'IssueSubtypeHasLinkedIssueTypeManagersError',
  'issueSubtypeHasIssueManagerOfIssueType',
  'issueTypeHasIssueManagerOfIssueSubtypes',
  'issueSubtypeAlreadyExistsError',
  'slaAlreadyExistsError',
  'invalidIssueTypeSubtype',
  'maintenanceModeError',
] as const;

export type ErrorUserInterfaceType =
  (typeof ERROR_USER_INTERFACE_VALUES)[number];

export type AppErrorType = (typeof APP_ERROR_TYPE_VALUES)[number];

export const ERROR_USER_INTERFACE: {
  [key: string]: ErrorUserInterfaceType;
} = {
  PAGE: 'page',
  DIALOG: 'dialog',
  TOAST: 'toast',
} as const;

export const APP_ERROR_TYPE: {
  [key: string]: AppErrorType;
} = {
  BAD_REQUEST: 'badRequest',
  FORBIDDEN: 'forbidden',
  INVALID_SOURCE_PARAMS: 'invalidSourceParams',
  ISSUE_NOT_FOUND: 'issueNotFound',
  NETWORK: 'network',
  NOT_FOUND: 'notFound',
  SCANNING_FILE: 'scanningFile',
  UNAUTHORIZED: 'unauthorized',
  UNAUTHORIZED_TO_VIEW_ISSUE: 'unauthorizedToViewIssue',
  UNAUTHORIZED_TO_MANAGE_ISSUE: 'unauthorizedToManageIssue',
  UNAUTHORIZED_TO_VIEW_RELATIONSHIPS: 'unauthorizedToViewRelationships',
  USER_CANNOT_UPDATE_ACTION: 'userCannotUpdateAction',
  ACTION_CANNOT_BE_APPROVED: 'actionCannotBeApproved',
  ACTION_CANNOT_BE_DECLINED: 'actionCannotBeDeclined',
  ACTION_CANNOT_BE_FINISHED: 'actionCannotBeFinished',
  ACTION_CANNOT_BE_STARTED: 'actionCannotBeStarted',
  MUTATION_ERROR: 'mutationError',
  ISSUE_ALREADY_CLOSED_ERROR: 'issueAlreadyClosedError',
  ISSUE_SLA_ALREADY_DISABLED_ERROR: 'issueSLAAlreadyDisabledError',
  ISSUE_SLA_ALREADY_ENABLED_ERROR: 'issueSLAAlreadyEnabledError',
  ISSUE_TEMPLATE_DUE_DATE_IS_GREATER_THAN_SLA:
    'issueTemplateDueDateIsGreaterThanSLA',
  ISSUE_TEMPLATE_ALREADY_EXISTS: 'issueTemplateAlreadyExists',
  ACTION_DUE_DATE_MUST_BE_LESS_OR_EQUAL_THAN_ISSUE_TEMPLATE_DUE_DATE:
    'actionDueDateMustBeLessOrEqualThanIssueTemplateDueDate',
  ISSUE_SUBTYPE_HAS_LINKED_ISSUE_MANAGERS_ERROR:
    'IssueSubtypeHasLinkedIssueTypeManagersError',
  ISSUE_SUBTYPE_HAS_ISSUE_MANAGER_OF_ISSUE_TYPE:
    'issueSubtypeHasIssueManagerOfIssueType',
  ISSUE_TYPE_HAS_ISSUE_MANAGER_OF_ISSUE_SUBTYPES:
    'issueTypeHasIssueManagerOfIssueSubtypes',
  ISSUE_SUBTYPE_ALREADY_EXISTS_ERROR: 'issueSubtypeAlreadyExistsError',
  SLA_ALREADY_EXISTS_ERROR: 'slaAlreadyExistsError',
  INVALID_ISSUE_TYPE_SUBTYPE: 'invalidIssueTypeSubtype',
  MAINTENANCE_MODE_ERROR: 'maintenanceModeError',
};

export interface AppErrorInterface {
  type: AppErrorType;
  userInterface?: ErrorUserInterfaceType;
}

export interface AppErrorHandlerProviderContextProps {
  error: AppErrorInterface | null;
  setError: (error: AppErrorInterface | null) => void;
}

export interface AppErrorHandlerProviderProps {
  children: ReactNode;
}

export type UseAppErrorHandler = () => AppErrorHandlerProviderContextProps;

export type ErrorByStatus = {
  type: AppErrorType;
  userInterface: ErrorUserInterfaceType;
  isDefault: boolean;
};

export type ErrorsByStatusesWithDefault = [
  { isDefault: true } & ErrorByStatus,
  ...ErrorByStatus[],
];
